import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import userAvatar from '@/assets/images/user-avatar.svg';
import moment from 'moment';
import { statusBadgeText } from '../../utils/BadgeEssay';
import { FiEdit3, FiEye, FiUpload } from 'react-icons/fi';
import { useClient } from 'jsonapi-react';
import { ModalWarning } from '@/components/custom/modal/modal-warning';
import usePermission from '@/hooks/usePermission';
import { TableRow, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { PlayfulCardButton } from '@/components/custom/playful-card';

// Mesma paleta soft usada nas demais telas de redação do professor (Playful) —
// duplicado de propósito, cada tela mantém seu próprio mapeamento visual.
const STATUS_BADGE_VARIANT = {
  accepting_proposal: 'info-soft',
  processing: 'amber-soft',
  reviewing: 'amber-soft',
  ocr_review: 'amber-soft',
  sending_by_teacher: 'amber-soft',
  unreviewed: 'success-soft',
  reviewed: 'success-soft',
  annulled: 'danger-soft',
  annulled_by_teacher: 'danger-soft',
  minimum_length_not_satisfied: 'danger-soft',
  failed_to_process: 'danger-soft',
  close_for_submissions: 'muted',
  not_sent: 'muted'
};

export function EssayMonitoringPlayfulCard({ student, essay, essayId, currentUser }) {
  const navigate = useNavigate();
  const [openWarning, setOpenWarning] = useState(false);
  const badgeVariant = STATUS_BADGE_VARIANT?.[student.status];
  const badgeText = statusBadgeText?.[student.status];
  const client = useClient();

  const canSend = usePermission('Enviar redação', 'Redação');

  const handleReviewEssay = () => {
    if (student.status === 'unreviewed') {
      client.mutate(['essays', essayId, 'essay_students', student.id], { status: 'reviewing' });
    }
    navigate(`/redacao/correcao/${student.id}/${essayId}`);
    setOpenWarning(false);
  };

  function renderAction() {
    const { status, id, corrector, user } = student;

    const viewEnabled =
      status === 'reviewed' || // corrigida pelo professor
      status === 'unreviewed' || // correção por IA
      status === 'annulled_by_teacher' || // anulada pelo professor
      status === 'reviewing' || // está sendo corrigida
      status === 'annulled'; // anulada

    const reviewEnabled =
      status === 'unreviewed' || // correção por IA
      (status === 'reviewing' && corrector.id === currentUser); // corrigindo e quem está corrigindo é o usuário atual

    const reachedSubmissionLimit = () => {
      const limit = essay['allow-multiple-submissions'] ? 2 : 1;
      return student['submission_order'] >= limit;
    };

    const isInvalid = () => ['annulled', 'annulled_by_teacher'].includes(student.status);

    const sendDisabled =
      !canSend || // não tem permissão
      (reachedSubmissionLimit() && status !== 'ocr_review' && status !== 'minimum_length_not_satisfied') || // já enviou a quantidade permitida e não está em revisão OCR e nem é inválida (mínimo de caracteres não atendido)
      status === 'reviewing' || // está sendo corrigida
      status === 'processing' || // está sendo processada
      (isInvalid() && reachedSubmissionLimit()) || // é inválida e já enviou a quantidade permitida
      status === 'close_for_submissions' || // prazo de envio encerrado
      essay.status === 'close_for_submissions' || // prazo de envio encerrado (status geral da redação)
      status === 'unreviewed'; // correção por IA

    return (
      <div className="flex flex-wrap gap-2">
        <PlayfulCardButton
          variant="outline"
          icon={FiUpload}
          onClick={() => navigate(`/redacao/enviar/${essayId}/${user?.id}`)}
          disabled={sendDisabled}
          className="h-9 w-auto gap-1.5 px-3 text-xs"
        >
          Enviar redação
        </PlayfulCardButton>
        <PlayfulCardButton
          variant="outline"
          icon={FiEye}
          onClick={() => navigate(`/redacao/visualizar/${id}/${essayId}`)}
          disabled={!viewEnabled}
          className="h-9 w-auto gap-1.5 px-3 text-xs"
        >
          Visualizar
        </PlayfulCardButton>
        <PlayfulCardButton
          variant={reviewEnabled ? undefined : 'outline'}
          icon={FiEdit3}
          onClick={() => {
            if (status === 'reviewing' && corrector.id === currentUser) {
              navigate(`/redacao/correcao/${id}/${essayId}`);
            } else {
              setOpenWarning(true);
            }
          }}
          disabled={!reviewEnabled}
          className="h-9 w-auto gap-1.5 px-3 text-xs"
        >
          Corrigir
        </PlayfulCardButton>
      </div>
    );
  }

  return (
    <>
      <TableRow>
        <TableCell>
          <div
            className="round-dg__user u-cursor-pointer"
            onClick={() => navigate(`/usuario/metricas/${student.user?.id}`)}
          >
            <img
              className="avatar avatar--xs"
              src={student.user?.image || userAvatar}
              alt="Imagem do usuário"
            />
            <div className="round-dg__user-inner">
              <h3
                className="round-dg__title"
                style={{ fontSize: 14 }}
                title={student.user?.name}
              >
                {student.user?.name}
              </h3>
            </div>
          </div>
        </TableCell>

        <TableCell>{student.status === 'ocr_review' ? student['submission_order'] - 1 : student['submission_order']}</TableCell>
        <TableCell>{student['delivery-date'] ? moment(student['delivery-date']).format('DD/MM/YYYY') : '-'}</TableCell>

        <TableCell>{student.grade}</TableCell>

        <TableCell>
          <Badge
            title={badgeText}
            variant={badgeVariant}
            className="shrink-0 rounded-full px-2.5 py-1 text-sm font-bold normal-case"
          >
            {badgeText}
          </Badge>
        </TableCell>

        <TableCell className="truncate">{student.corrector?.name || '-'}</TableCell>

        <TableCell>{renderAction()}</TableCell>
      </TableRow>

      <ModalWarning
        show={openWarning}
        onClose={() => setOpenWarning(false)}
        titleFirstButton="Não"
        titleSecondButton="Sim"
        title={`Deseja mesmo corrigir essa redação?`}
        description="Outros professores não poderão corrigi-la."
        onSubmitFirstButton={() => setOpenWarning(false)}
        onSubmitSecondButton={() => handleReviewEssay()}
      />
    </>
  );
}
